import { alpha, type Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { forwardRef, useEffect, useRef } from 'react';
import Breadcrumb from 'src/components/Breadcrumb';

interface MetaItem {
  label: string;
  value: string;
}

interface BreadcrumbItemType {
  label: string;
  onClick: () => void;
}

interface GalleryHeroProps {
  name: string;
  totalItems: number;
  metaItems?: MetaItem[];
  breadcrumbItems?: BreadcrumbItemType[];
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    overflow: 'hidden',
    background: theme.palette.background.default,
  },
  canvas: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  accentStrip: {
    position: 'relative',
    zIndex: 1,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 40%, transparent 100%)`,
  },
  content: {
    position: 'relative',
    zIndex: 1,
  },
  breadcrumb: {
    padding: '10px 24px 0',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 16,
    padding: '10px 24px 14px',
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: '-0.3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.primary,
  },
  inlineMeta: {
    display: 'flex',
    gap: 18,
    flexWrap: 'wrap',
    fontSize: 12,
    color: alpha(theme.palette.text.primary, 0.6),
    marginTop: 6,
  },
  metaLabel: {
    color: alpha(theme.palette.text.primary, 0.4),
    marginRight: 4,
  },
  quickStat: {
    textAlign: 'right',
    flexShrink: 0,
  },
  num: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.text.primary,
  },
  lbl: {
    fontSize: 11,
    color: alpha(theme.palette.text.primary, 0.4),
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginTop: 3,
  },
}));

// Parse a CSS color ("#RRGGBB", "#RGB", or "rgb(...)") into `rgba(r,g,b,a)` at the given alpha.
function toRgba(color: string, a: number): string {
  const trimmed = color.trim();
  if (trimmed.startsWith('rgb')) {
    const nums = trimmed.match(/\d+(\.\d+)?/g);
    if (nums && nums.length >= 3) {
      return `rgba(${nums[0]},${nums[1]},${nums[2]},${a})`;
    }
  }
  const hex = trimmed.replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

const GalleryHero = forwardRef<HTMLDivElement, GalleryHeroProps>(
  ({ name, totalItems, metaItems, breadcrumbItems }, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const draw = () => {
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, rect.width, rect.height);

        const primary = theme.palette.primary.main;
        const rawName = name || 'GALLERY';
        // Truncate long gallery names so the faded banner stays visually tidy. The faded text
        // is right-aligned and font auto-scales with width, so ~14 chars fit comfortably even
        // at narrower viewports; any extra clipping on the left edge just softens the fade.
        const text = (rawName.length > 14 ? `${rawName.slice(0, 12)}..` : rawName).toUpperCase();

        // Soft radial gradient — anchored top-right, tinted with primary
        const grad = ctx.createRadialGradient(
          rect.width * 0.85,
          rect.height * 0.3,
          30,
          rect.width * 0.85,
          rect.height * 0.3,
          rect.width * 0.8,
        );
        grad.addColorStop(0, toRgba(primary, 0.12));
        grad.addColorStop(1, toRgba(primary, 0));
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, rect.width, rect.height);

        // Giant faded gallery name. Cap by height (× 1.35) as well as width so the text fits
        // vertically when the banner is shorter than the normal ~117px (e.g. the shared-link
        // view has no breadcrumb row, so it's ~84px). Arial Black cap-height ≈ 0.72 × font,
        // so height × 1.35 keeps visible glyphs just inside the banner edges.
        const fontSize = Math.min(rect.width * 0.14, rect.height * 1.35, 160);
        ctx.font = `900 ${fontSize}px Impact, "Arial Black", Arial, sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = toRgba(primary, 0.07);
        ctx.fillText(text, rect.width - 20, rect.height / 2);

        // Subtle horizontal stripes for texture
        ctx.fillStyle = toRgba(primary, 0.03);
        for (let i = 0; i < rect.height; i += 8) {
          ctx.fillRect(0, i, rect.width, 1);
        }
      };

      draw();
      const ro = new ResizeObserver(draw);
      ro.observe(canvas);
      return () => ro.disconnect();
    }, [name, theme.palette.primary.main]);

    return (
      <div ref={ref} className={classes.root}>
        <canvas ref={canvasRef} className={classes.canvas} aria-hidden />
        <div className={classes.accentStrip} />
        <div className={classes.content}>
          {breadcrumbItems && breadcrumbItems.length > 0 && (
            <div className={classes.breadcrumb}>
              <Breadcrumb items={breadcrumbItems} />
            </div>
          )}
          <div className={classes.titleRow}>
            <div className={classes.titleBlock}>
              <div className={classes.name} title={name}>
                {name}
              </div>
              {metaItems && metaItems.length > 0 && (
                <div className={classes.inlineMeta}>
                  {metaItems.map((m) => (
                    <span key={m.label}>
                      <span className={classes.metaLabel}>{m.label}</span>
                      {m.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className={classes.quickStat}>
              <div className={classes.num}>{totalItems}</div>
              <div className={classes.lbl}>items</div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

GalleryHero.displayName = 'GalleryHero';

export default GalleryHero;
